import { SourcingCampaignIndex } from 'api-clients/monolith';
import { ReactNode } from 'react';
import { defineMessages, FormattedMessage, IntlShape } from 'react-intl';

import { ReachBreakdown } from '@/api-clients/get-reach-breakdown';
import { ChannelStatusPill } from '@/components/base/channel-status-pill';
import { ReturnBackArrowIcon } from '@/components/icons/return-back-arrow';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

import { MetricCell } from './columns';

const adLabels = defineMessages({
  google_search: {
    defaultMessage: 'Google Search',
    description: 'Google search advertising channel',
  },
  google_pmax: {
    defaultMessage: 'Google PMax',
    description: 'Google Performance Max advertising channel',
  },
  google_demand_gen: {
    defaultMessage: 'Google Demand Gen',
    description: 'Google Demand Gen advertising channel',
  },
  meta: {
    defaultMessage: 'Meta Ads',
    description: 'Advertising channel on Meta platforms',
  },
});

function CurrencyMetricCell({ value }: { value: number | null }) {
  const { formatCurrency } = useCurrencySymbol();
  return <MetricCell value={value} render={formatCurrency} />;
}

export function reachAdRowCells(
  row: ReachBreakdown['ad_rows'][number],
  intl: IntlShape,
): Record<string, ReactNode> {
  const date = (value: string) =>
    intl.formatDate(value, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  return {
    opening_name: (
      <div className="flex items-center gap-2">
        <ReturnBackArrowIcon className="text-fx-neutral-400 w-4 shrink-0" />
        <Typography variant="body1" className="whitespace-normal">
          {row.ad_type === 'unknown'
            ? row.label
            : intl.formatMessage(adLabels[row.ad_type])}
        </Typography>
      </div>
    ),
    channel: (
      <div className="flex items-center gap-1">
        {row.provider && (
          <ChannelIcon
            channelSlug={row.provider}
            channelName={row.provider === 'google' ? 'Google' : 'Meta'}
            size="xs"
          />
        )}
        <Typography variant="small2">
          <FormattedMessage
            defaultMessage="via Reach"
            description="Ad channel managed through the Reach advertising service"
          />
        </Typography>
      </div>
    ),
    status: <ChannelStatusPill status={row.status} />,
    budget: <CurrencyMetricCell value={row.budget} />,
    start_date: (
      <Typography variant="subtitle1">{date(row.start_date)}</Typography>
    ),
    end_date: (
      <Typography variant="subtitle1">
        {row.end_date
          ? date(row.end_date)
          : intl.formatMessage({
              defaultMessage: 'No end date',
              description:
                'Advertising campaign runs without a scheduled end date',
            })}
      </Typography>
    ),
    spend: <CurrencyMetricCell value={row.spend} />,
    applicants: (
      <MetricCell value={row.applications} render={intl.formatNumber} />
    ),
    cpa: <CurrencyMetricCell value={row.applications === 0 ? null : row.cpa} />,
    hires: <MetricCell value={row.hires} render={intl.formatNumber} />,
    cph: <CurrencyMetricCell value={row.hires === 0 ? null : row.cph} />,
    conversion: (
      <MetricCell value={row.conversion} render={value => `${value}%`} />
    ),
  };
}

export function otherRowCells(
  other: NonNullable<ReachBreakdown['other_row']>,
  intl: IntlShape,
): Record<string, ReactNode> {
  return {
    opening_name: (
      <Typography variant="body1" className="whitespace-normal">
        <FormattedMessage
          defaultMessage="Other Reach applicants"
          description="Applicants attributed to Reach but not to an individual advertising channel"
        />
      </Typography>
    ),
    applicants: (
      <MetricCell value={other.applications} render={intl.formatNumber} />
    ),
    hires: <MetricCell value={other.hires} render={intl.formatNumber} />,
    conversion: (
      <MetricCell value={other.conversion} render={value => `${value}%`} />
    ),
  };
}

export function loadingRowCells(): Record<string, ReactNode> {
  return { opening_name: <Skeleton className="h-3 w-full" /> };
}

export function errorRowCells(): Record<string, ReactNode> {
  return {
    opening_name: (
      <Typography variant="small2" className="whitespace-normal" role="status">
        <FormattedMessage
          defaultMessage="Couldn't load ad channels."
          description="Reach advertising channel details could not be retrieved"
        />
      </Typography>
    ),
  };
}

export function ReachRowCaption({ count }: { count: number }) {
  return (
    <Typography
      variant="small2"
      className="text-fx-neutral-600 whitespace-normal"
    >
      <FormattedMessage
        defaultMessage="Totals rolled up from {count, plural, one {# ad channel} other {# ad channels}}"
        description="Campaign totals combine the specified number of advertising channels"
        values={{ count }}
      />
    </Typography>
  );
}

export function ReachCountBadge({ count }: { count: number }) {
  return <Tag className="bg-fx-neutral-100 shrink-0" text={count} />;
}

export function reachRowFigures(
  indexRow: SourcingCampaignIndex,
  totals: ReachBreakdown['totals'] | null,
) {
  return {
    spend: totals?.spend ?? indexRow.spend,
    applications: totals?.applications ?? indexRow.applications,
    hires: totals?.hires ?? indexRow.hires,
    cpa: totals?.cpa ?? indexRow.cpa,
    cph: totals?.cph ?? indexRow.cph,
    conversion: totals?.conversion ?? indexRow.conversion,
  };
}
